// src/modulos/Cadastros/AccountExecutivePage.js

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import styles from './Registers.module.css';
import { FiEdit, FiTrash2, FiPlus } from 'react-icons/fi';
import { FaPeopleGroup } from "react-icons/fa6";
import { HiOutlineIdentification } from 'react-icons/hi';
import { IoMdSettings } from "react-icons/io";

// Common Components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
    Toast,
} from '../../components/common';

// --- Constantes ---
const EXECUTIVES_COLLECTION = 'account-executives';
const CLIENTS_COLLECTION = 'clients';
const REGIONS_COLLECTION = 'parameters/data/regioes';
const ITEMS_PER_PAGE = 10;
const CLIENTS_PER_PAGE = 10;

const DEFAULT_EXECUTIVE_MODEL = {
    emailUsuario: '',
    nome: '',
    rg: '',
    cpf: '',
    valorFixo: '',
    comissaoPadrao: '',
    codBanco: '',
    comSupervisor: '',
    agencia: '',
    conta: '',
    utilizaSelosEspeciais: false,
    excluirClientesOutrosExecutivos: false,
    visualizarTodosClientes: false,
    situacao: 'ativo',
    observacoes: '',
    clientesVinculados: [],
    regioesVinculadas: [],
};

const selectStyles = {
    control: (base) => ({ ...base, borderColor: '#e9ecef', minHeight: '40px', fontSize: '1rem' }),
    multiValue: (base) => ({ ...base, backgroundColor: '#e2e8f0' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const isEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true;
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    for (let key of keys1) {
        const val1 = obj1[key];
        const val2 = obj2[key];
        if (Array.isArray(val1) && Array.isArray(val2)) {
            if (val1.length !== val2.length) return false;
            const sorted1 = [...val1].sort();
            const sorted2 = [...val2].sort();
            for (let i = 0; i < sorted1.length; i++) {
                if (JSON.stringify(sorted1[i]) !== JSON.stringify(sorted2[i])) return false;
            }
        } else if (val1 !== val2) {
            return false;
        }
    }
    return true;
};

const sanitizeDocument = (value) => {
    if (!value) return '';
    return value.replace(/\D/g, '');
};

const AccountExecutivePage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('executivos_de_conta');

    // --- State ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formStatus, setFormStatus] = useState({ message: '', type: '' });
    const [originalData, setOriginalData] = useState(null);
    const [executiveData, setExecutiveData] = useState(DEFAULT_EXECUTIVE_MODEL);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [clientsPage, setClientsPage] = useState(1);
    const [shouldLoadClients, setShouldLoadClients] = useState(false);
    const [shouldLoadRegions, setShouldLoadRegions] = useState(false);
    const [toasts, setToasts] = useState([]);

    // --- Data Hooks ---
    const {
        data: executives,
        loading: executivesLoading,
        formLoading,
        addDocument,
        updateDocument,
        deleteDocument,
    } = useFirestore(EXECUTIVES_COLLECTION, 'nome');

    const {
        data: clientsList,
        loading: clientsLoading,
    } = useFirestore(shouldLoadClients ? CLIENTS_COLLECTION : null, 'razaoSocial');

    const {
        data: regionsList,
        loading: regionsLoading,
    } = useFirestore(shouldLoadRegions ? REGIONS_COLLECTION : null, 'name');

    // --- Toast Helpers ---
    const addToast = (message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    // --- Effects ---
    useEffect(() => {
        if (isModalOpen) {
            setShouldLoadClients(true);
            setShouldLoadRegions(true);
            setClientsPage(1);
        }
    }, [isModalOpen]);

    useEffect(() => {
        if (!isModalOpen) {
            const timer = setTimeout(() => {
                setShouldLoadClients(false);
                setShouldLoadRegions(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isModalOpen]);

    // --- Memoized Data ---
    const regionOptions = useMemo(() => {
        if (!regionsList) return [];
        return regionsList.map(region => ({
            value: region.id,
            label: region.name || 'Sem Nome'
        }));
    }, [regionsList]);

    const linkedClientsWithDetails = useMemo(() => {
        if (!clientsList || !editingItem?.id) return [];
        const clientsOfExecutive = clientsList.filter(
            client => client.accountExecutiveId === editingItem.id
        );
        const legacyLinkedClients = editingItem.clientesVinculados || [];
        const allClientIds = new Set();
        const combinedClients = [];
        clientsOfExecutive.forEach(client => {
            if (!allClientIds.has(client.id)) {
                allClientIds.add(client.id);
                combinedClients.push({
                    id: client.id,
                    codigo: client.codigo || '-',
                    razaoSocial: client.razaoSocial || client.nomeFantasia || 'Cliente',
                    cnpjCpf: client.cnpjCpf || '-',
                    situacao: client.situacao || '-',
                    comissaoPersonalizada: '',
                    usaComissaoPadrao: true,
                });
            }
        });
        legacyLinkedClients.forEach(linkedClient => {
            if (!allClientIds.has(linkedClient.clientId)) {
                const client = clientsList.find(c => c.id === linkedClient.clientId);
                if (client) {
                    allClientIds.add(linkedClient.clientId);
                    combinedClients.push({
                        id: linkedClient.clientId,
                        codigo: client.codigo || '-',
                        razaoSocial: client.razaoSocial || client.nomeFantasia || 'Cliente não encontrado',
                        cnpjCpf: client.cnpjCpf || '-',
                        situacao: client.situacao || '-',
                        comissaoPersonalizada: linkedClient.comissaoPersonalizada,
                        usaComissaoPadrao: linkedClient.usaComissaoPadrao !== false,
                    });
                }
            }
        });
        return combinedClients;
    }, [clientsList, editingItem?.id, editingItem?.clientesVinculados]);

    const paginatedLinkedClients = useMemo(() => {
        const startIndex = (clientsPage - 1) * CLIENTS_PER_PAGE;
        return linkedClientsWithDetails.slice(startIndex, startIndex + CLIENTS_PER_PAGE);
    }, [linkedClientsWithDetails, clientsPage]);

    const totalClientsPages = Math.ceil(linkedClientsWithDetails.length / CLIENTS_PER_PAGE);

    useEffect(() => {
        setClientsPage(1);
    }, [editingItem?.id, linkedClientsWithDetails.length]);

    // --- Handlers ---
    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        let processedValue = type === 'checkbox' ? checked : value;
        if (name === 'cpf' || name === 'rg') {
            processedValue = sanitizeDocument(value);
        }
        if (name === 'valorFixo' || name === 'comissaoPadrao' || name === 'comSupervisor') {
            processedValue = value === '' ? '' : parseFloat(value);
        }
        setExecutiveData((prev) => ({
            ...prev,
            [name]: processedValue,
        }));
    }, []);

    const handleRegionsChange = useCallback((selectedOptions) => {
        const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
        setExecutiveData((prev) => ({
            ...prev,
            regioesVinculadas: selectedIds
        }));
    }, []);

    const handleClientCommissionChange = useCallback((clientId, field, value) => {
        setExecutiveData(prev => ({
            ...prev,
            clientesVinculados: prev.clientesVinculados.map(client => {
                if (client.clientId === clientId) {
                    if (field === 'usaComissaoPadrao') {
                        return {
                            ...client,
                            usaComissaoPadrao: value,
                            comissaoPersonalizada: value ? '' : client.comissaoPersonalizada
                        };
                    }
                    if (field === 'comissaoPersonalizada') {
                        return {
                            ...client,
                            comissaoPersonalizada: value === '' ? '' : parseFloat(value),
                            usaComissaoPadrao: false
                        };
                    }
                }
                return client;
            })
        }));
    }, []);

    const resetForm = useCallback(() => {
        setExecutiveData(DEFAULT_EXECUTIVE_MODEL);
        setEditingItem(null);
        setOriginalData(null);
        setIsModalOpen(false);
        setFormStatus({ message: '', type: '' });
        setIsSubmitting(false);
        setClientsPage(1);
    }, []);

    const handleNewClick = useCallback(() => {
        if (!can.create) {
            addToast(t('pages.accountExecutives.noPermissionCreate'), 'error');
            return;
        }
        setEditingItem(null);
        setExecutiveData({ ...DEFAULT_EXECUTIVE_MODEL });
        setOriginalData(null);
        setFormStatus({ message: '', type: '' });
        setIsModalOpen(true);
    }, [can.create, t, addToast]);

    const handleEditClick = useCallback((item) => {
        if (!can.edit) {
            addToast(t('pages.accountExecutives.noPermissionEdit'), 'error');
            return;
        }
        let clientesVinculados = item.clientesVinculados || [];
        if (clientesVinculados.length > 0 && typeof clientesVinculados[0] === 'string') {
            clientesVinculados = clientesVinculados.map(clientId => ({
                clientId: clientId,
                comissaoPersonalizada: '',
                usaComissaoPadrao: true,
            }));
        }
        const preparedItem = {
            ...DEFAULT_EXECUTIVE_MODEL,
            ...item,
            clientesVinculados: clientesVinculados,
            regioesVinculadas: item.regioesVinculadas || [],
            valorFixo: item.valorFixo !== undefined && item.valorFixo !== '' ? parseFloat(item.valorFixo) : '',
            comissaoPadrao: item.comissaoPadrao !== undefined && item.comissaoPadrao !== '' ? parseFloat(item.comissaoPadrao) : '',
            comSupervisor: item.comSupervisor !== undefined && item.comSupervisor !== '' ? parseFloat(item.comSupervisor) : '',
        };
        setEditingItem(item);
        setExecutiveData(preparedItem);
        setOriginalData(JSON.parse(JSON.stringify(preparedItem)));
        setFormStatus({ message: '', type: '' });
        setIsModalOpen(true);
    }, [can.edit, t, addToast]);

    const hasChanges = useCallback(() => {
        if (!editingItem) {
            return !isEqual(executiveData, DEFAULT_EXECUTIVE_MODEL);
        }
        if (!originalData) return false;
        return !isEqual(executiveData, originalData);
    }, [executiveData, editingItem, originalData]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!editingItem && !can.create) {
            setFormStatus({ message: t('pages.accountExecutives.noPermissionCreate'), type: 'error' });
            return;
        }
        if (editingItem && !can.edit) {
            setFormStatus({ message: t('pages.accountExecutives.noPermissionEdit'), type: 'error' });
            return;
        }
        if (isSubmitting) return;
        setFormStatus({ message: '', type: '' });
        if (!executiveData.nome.trim()) {
            setFormStatus({ message: t('pages.accountExecutives.form.name') + ' ' + t('common.required'), type: 'error' });
            return;
        }
        if (!hasChanges()) {
            setFormStatus({ message: t('common.noChanges'), type: 'info' });
            setTimeout(() => resetForm(), 1500);
            return;
        }
        setIsSubmitting(true);
        const dataToSave = { ...executiveData };
        if (dataToSave.valorFixo === '') dataToSave.valorFixo = null;
        if (dataToSave.comissaoPadrao === '') dataToSave.comissaoPadrao = null;
        if (dataToSave.comSupervisor === '') dataToSave.comSupervisor = null;
        try {
            if (editingItem) {
                await updateDocument(editingItem.id, dataToSave);
                setFormStatus({ message: t('pages.accountExecutives.updateSuccess'), type: 'success' });
                addToast(t('pages.accountExecutives.updateSuccess'), 'success');
            } else {
                await addDocument(dataToSave);
                setFormStatus({ message: t('pages.accountExecutives.createSuccess'), type: 'success' });
                addToast(t('pages.accountExecutives.createSuccess'), 'success');
            }
            setTimeout(() => resetForm(), 1500);
        } catch (err) {
            setFormStatus({ message: `${t('pages.accountExecutives.updateError')}: ${err.message}`, type: 'error' });
            addToast(`${t('pages.accountExecutives.updateError')}: ${err.message}`, 'error');
        } finally {
            setIsSubmitting(false);
        }
    }, [executiveData, editingItem, addDocument, updateDocument, resetForm, hasChanges, isSubmitting, can.create, can.edit, t, addToast]);

    const handleDeleteClick = async (item) => {
        if (!can.delete) {
            addToast(t('pages.accountExecutives.noPermissionDelete'), 'error');
            return;
        }
        if (window.confirm(t('pages.accountExecutives.deleteConfirm'))) {
            try {
                const result = await deleteDocument(item.id);
                if (!result.success) {
                    addToast(`${t('pages.accountExecutives.deleteError')}: ${result.error || t('common.error')}`, 'error');
                } else {
                    addToast(t('pages.accountExecutives.deleteSuccess'), 'success');
                }
            } catch (err) {
                addToast(`${t('pages.accountExecutives.deleteError')}: ${err.message}`, 'error');
            }
        }
    };

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    // --- Filtering ---
    const filteredExecutives = useMemo(() => {
        if (!executives) return [];
        const lowerSearch = searchTerm.toLowerCase().trim();
        if (!lowerSearch) return executives;
        return executives.filter((exec) => {
            const nomeMatch = exec.nome?.toLowerCase().includes(lowerSearch) || false;
            const emailMatch = exec.emailUsuario?.toLowerCase().includes(lowerSearch) || false;
            const cpfMatch = exec.cpf?.toLowerCase().includes(lowerSearch) || false;
            const rgMatch = exec.rg?.toLowerCase().includes(lowerSearch) || false;
            return nomeMatch || emailMatch || cpfMatch || rgMatch;
        });
    }, [executives, searchTerm]);

    const totalPages = Math.ceil(filteredExecutives.length / ITEMS_PER_PAGE);
    const currentExecutives = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredExecutives.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredExecutives, currentPage]);

    // --- Render ---
    const renderTable = () => {
        if (executivesLoading) {
            return <TableSkeleton columns={7} rows={ITEMS_PER_PAGE} />;
        }

        if (currentExecutives.length === 0) {
            return (
                <EmptyState
                    icon="👤"
                    title={t('pages.accountExecutives.table.empty')}
                    message={searchTerm ? t('pages.accountExecutives.search.noResults') : t('pages.accountExecutives.table.emptyMessage')}
                    actionText={can.create ? t('pages.accountExecutives.newExecutive') : undefined}
                    onAction={can.create ? handleNewClick : undefined}
                />
            );
        }

        return (
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>{t('pages.accountExecutives.table.name')}</th>
                        <th>{t('pages.accountExecutives.table.email')}</th>
                        <th>{t('pages.accountExecutives.table.cpf')}</th>
                        <th>{t('pages.accountExecutives.table.defaultCommission')}</th>
                        <th>{t('pages.accountExecutives.table.clients')}</th>
                        <th>{t('pages.accountExecutives.table.situation')}</th>
                        <th style={{ width: '150px' }}>{t('pages.accountExecutives.table.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {currentExecutives.map((item) => (
                        <tr key={item.id}>
                            <td>{item.nome}</td>
                            <td>{item.emailUsuario || '-'}</td>
                            <td>{item.cpf || '-'}</td>
                            <td>
                                {item.comissaoPadrao !== null && item.comissaoPadrao !== undefined
                                    ? `${parseFloat(item.comissaoPadrao).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%`
                                    : '-'}
                            </td>
                            <td>{item.clientesVinculados?.length || '-'}</td>
                            <td>
                                <span
                                    style={{
                                        display: 'inline-block',
                                        padding: '3px 10px',
                                        borderRadius: '12px',
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        backgroundColor: item.situacao === 'ativo' ? '#d4edda' : '#f8d7da',
                                        color: item.situacao === 'ativo' ? '#155724' : '#721c24',
                                    }}
                                >
                                    {item.situacao === 'ativo' ? t('pages.accountExecutives.table.active') : t('pages.accountExecutives.table.inactive')}
                                </span>
                            </td>
                            <td>
                                <div className={styles.actionsContainer}>
                                    {can.edit && (
                                        <button
                                            onClick={() => handleEditClick(item)}
                                            className={styles.actionButton}
                                            disabled={formLoading}
                                        >
                                            <FiEdit />
                                        </button>
                                    )}
                                    {can.delete && (
                                        <button
                                            onClick={() => handleDeleteClick(item)}
                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                            disabled={formLoading}
                                        >
                                            <FiTrash2 />
                                        </button>
                                    )}
                                    {!can.edit && !can.delete && (
                                        <span className={styles.textMuted}>{t('pages.accountExecutives.noActions')}</span>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    // --- Modal Content ---
    const renderModalContent = () => {
        const selectedRegionsOptions = regionOptions.filter(option =>
            executiveData.regioesVinculadas?.includes(option.value)
        );
        const isLoading = formLoading || isSubmitting;
        const isRegionsDataLoading = shouldLoadRegions && regionsLoading;
        const isClientsDataLoading = shouldLoadClients && clientsLoading;

        return (
            <div className={styles.formContent}>
                {isClientsDataLoading && (
                    <div className={styles.infoBox} style={{ margin: '0 32px 20px 32px' }}>
                        <span className={styles.alertIcon}>🔄</span>
                        {t('pages.accountExecutives.loading')}
                    </div>
                )}

                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><HiOutlineIdentification /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.accountExecutives.form.name')}</h3>
                        <span className={styles.sectionBadge}>{t('common.required')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                            <label>{t('pages.accountExecutives.form.email')}</label>
                            <input
                                type="email"
                                name="emailUsuario"
                                value={executiveData.emailUsuario}
                                onChange={handleInputChange}
                                className={styles.input}
                                placeholder={t('pages.accountExecutives.form.emailPlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                            <label>{t('pages.accountExecutives.form.name')} <span className={styles.required}>*</span></label>
                            <input
                                type="text"
                                name="nome"
                                value={executiveData.nome}
                                onChange={handleInputChange}
                                className={styles.input}
                                placeholder={t('pages.accountExecutives.form.namePlaceholder')}
                                disabled={isLoading}
                                required
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.accountExecutives.form.rg')}</label>
                            <input
                                type="text"
                                name="rg"
                                value={executiveData.rg}
                                onChange={handleInputChange}
                                className={styles.input}
                                placeholder={t('pages.accountExecutives.form.rgPlaceholder')}
                                disabled={isLoading}
                                maxLength={20}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.accountExecutives.form.cpf')}</label>
                            <input
                                type="text"
                                name="cpf"
                                value={executiveData.cpf}
                                onChange={handleInputChange}
                                className={styles.input}
                                placeholder={t('pages.accountExecutives.form.cpfPlaceholder')}
                                disabled={isLoading}
                                maxLength={11}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.accountExecutives.form.fixedValue')}</label>
                            <input
                                type="number"
                                name="valorFixo"
                                value={executiveData.valorFixo === '' ? '' : executiveData.valorFixo}
                                onChange={handleInputChange}
                                className={styles.input}
                                placeholder={t('pages.accountExecutives.form.fixedValuePlaceholder')}
                                step="0.01"
                                min="0"
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.accountExecutives.form.defaultCommission')}</label>
                            <input
                                type="number"
                                name="comissaoPadrao"
                                value={executiveData.comissaoPadrao === '' ? '' : executiveData.comissaoPadrao}
                                onChange={handleInputChange}
                                className={styles.input}
                                placeholder={t('pages.accountExecutives.form.defaultCommissionPlaceholder')}
                                step="0.01"
                                min="0"
                                max="100"
                                disabled={isLoading}
                            />
                            <small className={styles.fieldHint}>{t('pages.accountExecutives.form.defaultCommission')}</small>
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.accountExecutives.form.bankCode')}</label>
                            <input
                                type="text"
                                name="codBanco"
                                value={executiveData.codBanco}
                                onChange={handleInputChange}
                                className={styles.input}
                                placeholder={t('pages.accountExecutives.form.bankCodePlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.accountExecutives.form.supervisorCommission')}</label>
                            <input
                                type="number"
                                name="comSupervisor"
                                value={executiveData.comSupervisor === '' ? '' : executiveData.comSupervisor}
                                onChange={handleInputChange}
                                className={styles.input}
                                placeholder={t('pages.accountExecutives.form.supervisorCommissionPlaceholder')}
                                step="0.01"
                                min="0"
                                max="100"
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.accountExecutives.form.agency')}</label>
                            <input
                                type="text"
                                name="agencia"
                                value={executiveData.agencia}
                                onChange={handleInputChange}
                                className={styles.input}
                                placeholder={t('pages.accountExecutives.form.agencyPlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.accountExecutives.form.account')}</label>
                            <input
                                type="text"
                                name="conta"
                                value={executiveData.conta}
                                onChange={handleInputChange}
                                className={styles.input}
                                placeholder={t('pages.accountExecutives.form.accountPlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FaPeopleGroup /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.accountExecutives.form.clients')}</h3>
                        <span className={styles.sectionBadge}>{linkedClientsWithDetails.length}</span>
                    </div>
                    <div style={{ padding: '20px' }}>
                        {renderClientsTable()}
                    </div>
                </div>

                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><IoMdSettings /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.accountExecutives.form.regions')}</h3>
                    </div>
                    <div style={{ padding: '20px' }}>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.accountExecutives.form.regions')}</label>
                            <Select
                                isMulti
                                options={regionOptions}
                                value={selectedRegionsOptions}
                                onChange={handleRegionsChange}
                                placeholder={isRegionsDataLoading ? t('common.loading') : t('pages.accountExecutives.form.regionsPlaceholder')}
                                styles={selectStyles}
                                isDisabled={isLoading || isRegionsDataLoading}
                                noOptionsMessage={() => t('common.noOptions')}
                                menuPortalTarget={document.body}
                            />
                        </div>
                        <div className={styles.radioGroup} style={{ marginTop: '20px', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <label className={styles.sectionLabel}>{t('pages.accountExecutives.form.situation')}</label>
                            <div className={styles.radioGroup}>
                                <label className={styles.radioLabel}>
                                    <input
                                        type="radio"
                                        name="situacao"
                                        value="ativo"
                                        checked={executiveData.situacao === 'ativo'}
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                    <span>{t('pages.accountExecutives.table.active')}</span>
                                </label>
                                <label className={styles.radioLabel}>
                                    <input
                                        type="radio"
                                        name="situacao"
                                        value="inativo"
                                        checked={executiveData.situacao === 'inativo'}
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                    <span>{t('pages.accountExecutives.table.inactive')}</span>
                                </label>
                            </div>
                        </div>
                        <div className={styles.inputGroup} style={{ marginTop: '20px' }}>
                            <label>{t('pages.accountExecutives.form.observations')}</label>
                            <textarea
                                name="observacoes"
                                value={executiveData.observacoes}
                                onChange={handleInputChange}
                                className={styles.textarea}
                                placeholder={t('pages.accountExecutives.form.observationsPlaceholder')}
                                disabled={isLoading}
                                rows={4}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderClientsTable = () => {
        if (linkedClientsWithDetails.length === 0) {
            return (
                <div className={styles.emptyState} style={{ padding: '40px', textAlign: 'center' }}>
                    <p className={styles.emptyMessage}>{t('pages.accountExecutives.clientsList.empty')}</p>
                    <small className={styles.fieldHint}>
                        {t('pages.accountExecutives.clientsList.hint')}
                    </small>
                </div>
            );
        }

        return (
            <>
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>{t('pages.accountExecutives.clientsList.code')}</th>
                                <th>{t('pages.accountExecutives.clientsList.companyName')}</th>
                                <th>{t('pages.accountExecutives.clientsList.document')}</th>
                                <th>{t('pages.accountExecutives.clientsList.situation')}</th>
                                <th style={{ width: '200px' }}>{t('pages.accountExecutives.clientsList.commission')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedLinkedClients.map((client) => {
                                const effectiveCommission = client.usaComissaoPadrao
                                    ? executiveData.comissaoPadrao
                                    : client.comissaoPersonalizada;

                                return (
                                    <tr key={client.id}>
                                        <td>{client.codigo}</td>
                                        <td>{client.razaoSocial}</td>
                                        <td>{client.cnpjCpf}</td>
                                        <td>
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    padding: '3px 10px',
                                                    borderRadius: '12px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                    backgroundColor: client.situacao === 'Ativo' ? '#d4edda' : '#f8d7da',
                                                    color: client.situacao === 'Ativo' ? '#155724' : '#721c24',
                                                }}
                                            >
                                                {client.situacao}
                                            </span>
                                        </td>
                                        <td>
                                            {can.edit ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                    <label className={styles.checkboxLabel} style={{ fontSize: '0.8rem' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={client.usaComissaoPadrao}
                                                            onChange={(e) => handleClientCommissionChange(client.id, 'usaComissaoPadrao', e.target.checked)}
                                                            disabled={isSubmitting}
                                                        />
                                                        <span>{t('pages.accountExecutives.form.useDefault', { value: executiveData.comissaoPadrao || '0' })}</span>
                                                    </label>
                                                    {!client.usaComissaoPadrao && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                max="100"
                                                                value={client.comissaoPersonalizada}
                                                                onChange={(e) => handleClientCommissionChange(client.id, 'comissaoPersonalizada', e.target.value)}
                                                                placeholder={t('pages.accountExecutives.form.customCommission')}
                                                                className={styles.input}
                                                                style={{ width: '100px' }}
                                                                disabled={isSubmitting}
                                                            />
                                                            <span>%</span>
                                                        </div>
                                                    )}
                                                    {client.usaComissaoPadrao && (
                                                        <span style={{ fontSize: '0.85rem', color: '#28a745' }}>
                                                            {t('pages.accountExecutives.clientsList.commission')}: {effectiveCommission || '0'}%
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{ fontSize: '0.85rem' }}>
                                                    {t('pages.accountExecutives.clientsList.commission')}: {effectiveCommission || '0'}%
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {totalClientsPages > 1 && (
                    <div className={styles.paginationControls} style={{ marginTop: '16px', marginBottom: '0' }}>
                        <button
                            type="button"
                            className={styles.actionButton}
                            onClick={() => setClientsPage((p) => Math.max(p - 1, 1))}
                            disabled={clientsPage === 1 || isSubmitting}
                        >
                            {t('pages.accountExecutives.pagination.previous')}
                        </button>
                        <span>{t('pages.accountExecutives.pagination.page', { current: clientsPage, total: totalClientsPages })} ({linkedClientsWithDetails.length} {t('pages.accountExecutives.clientsList.title').toLowerCase()})</span>
                        <button
                            type="button"
                            className={styles.actionButton}
                            onClick={() => setClientsPage((p) => Math.min(p + 1, totalClientsPages))}
                            disabled={clientsPage === totalClientsPages || isSubmitting}
                        >
                            {t('pages.accountExecutives.pagination.next')}
                        </button>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className={styles.pageContainer}>
            {/* Toast Notifications */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={resetForm}
                onSubmit={handleSubmit}
                title={editingItem ? t('pages.accountExecutives.editExecutive') : t('pages.accountExecutives.newExecutive')}
                subtitle={editingItem ? `${t('pages.accountExecutives.editExecutive')}: ${executiveData.nome}` : undefined}
                isLoading={isSubmitting || formLoading}
                error={formStatus.type === 'error' ? formStatus.message : ''}
                success={formStatus.type === 'success' ? formStatus.message : ''}
                submitText={editingItem ? t('pages.accountExecutives.form.save') : t('pages.accountExecutives.form.create')}
                maxWidth="860px"
            >
                {renderModalContent()}
            </FormModal>

            {/* Page Header */}
            <PageHeader
                title={t('pages.accountExecutives.title')}
                actionText={can.create ? t('pages.accountExecutives.newExecutive') : undefined}
                onAction={handleNewClick}
                disabled={executivesLoading || formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <SearchBar
                placeholder={t('pages.accountExecutives.search.placeholder')}
                onSearch={handleSearch}
                disabled={executivesLoading}
                debounceDelay={300}
            />

            {/* Table */}
            <div className={styles.tableContainer}>
                {renderTable()}
            </div>

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredExecutives.length}
                onPageChange={setCurrentPage}
                disabled={executivesLoading}
            />
        </div>
    );
};

export default AccountExecutivePage;